import { AlertCircle, Check, X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

import { EmptyState, LoadingCards } from "components/states";
import { Button } from "components/ui/button";

import { WarningNote } from "./WarningNote";

import type { SeatRequestListState } from "../../seatings/seat-requests";
import type { RequestListState } from "../../track-switch/use-track-switch";
import { AdminCommandFeedback } from "../AdminCommandFeedback";
import { RequestList, type AdminRequestRow } from "../requests/RequestList";
import { seatChangeRows } from "../requests/seat-change-rows";
import {
  sortRequestRows,
  trackSwitchRows,
} from "../requests/track-switch-rows";
import { useAdminCommand } from "../use-admin-command";
import { useAdminTracks } from "../use-admin-content";
import { useAttendeeRecords } from "../use-attendee-records";

/**
 * The Requests tab: what attendees asked the organisers for, with Accept and
 * Decline, in one list over two types.
 *
 * - Table switch requests (TNG-92). Accepting files DECIDE_TRACK_SWITCH,
 *   which moves the attendee the way ASSIGN_ATTENDEE_TRACKS does. Tables have
 *   no capacity, so each row shows how many people already sit there.
 * - Dinner seat changes (TNG-94). Accepting files DECIDE_SEAT_REQUEST, which
 *   moves and pins the attendee in the draft and publishes that one move;
 *   each row shows how many seats are free at the dinner table asked for.
 *
 * Both lists are read by EventAdminPage, which also counts the open ones for
 * the tab's badge, so the tab and its badge never disagree.
 */
export function RequestsSection({
  eventId,
  uid,
  timeZone,
  requests,
  seatRequests = { status: "success", data: [], failed: [] },
}: {
  eventId: string;
  uid: string;
  timeZone?: string;
  requests: RequestListState;
  seatRequests?: SeatRequestListState;
}) {
  const tracks = useAdminTracks(eventId);
  const attendees = useAttendeeRecords(eventId);
  const command = useAdminCommand();
  const [answering, setAnswering] = useState<string | null>(null);
  const busy =
    command.state.status === "submitting" ||
    command.state.status === "watching";

  // An accepted request moves somebody, so the current tables and the seat
  // counts are read again once the command has applied.
  const reloadAttendees = attendees.reload;
  useEffect(() => {
    if (command.state.status === "succeeded") reloadAttendees();
    if (
      command.state.status !== "submitting" &&
      command.state.status !== "watching"
    ) {
      setAnswering(null);
    }
  }, [command.state.status, reloadAttendees]);

  // Each type shows as soon as it has loaded, and a failed read of one
  // never hides the other.
  const rows = useMemo(() => {
    const roster = attendees.status === "success" ? attendees.data : [];
    return sortRequestRows([
      ...(requests.status === "success"
        ? trackSwitchRows({
            requests: requests.data,
            attendees: roster,
            tracks: tracks.status === "success" ? tracks.data : [],
            timeZone,
          })
        : []),
      ...(seatRequests.status === "success"
        ? seatChangeRows({
            entries: seatRequests.data,
            attendees: roster,
            timeZone,
          })
        : []),
    ]);
  }, [requests, seatRequests, attendees, tracks, timeZone]);

  if (requests.status === "loading" && seatRequests.status === "loading")
    return <LoadingCards cards={2} />;
  // Neither type could be read: the warnings below name both, and an empty
  // list would claim there is nothing open.
  const nothingLoaded =
    requests.status === "error" && seatRequests.status === "error";
  const problems = [
    requests.status === "error"
      ? `Theme table switch requests could not be loaded: ${requests.error}`
      : null,
    seatRequests.status === "error"
      ? `Dinner seat change requests could not be loaded: ${seatRequests.error}`
      : null,
    ...(seatRequests.status === "success"
      ? seatRequests.failed.map(
          (name) =>
            `Dinner seat change requests for ${name} could not be loaded.`,
        )
      : []),
  ].filter((problem): problem is string => problem !== null);
  const stillLoading =
    requests.status === "loading" || seatRequests.status === "loading";

  const decide = (row: AdminRequestRow, decision: "ACCEPT" | "DECLINE") => {
    const answering = `${row.type}:${row.id}:${decision}`;
    if (row.type === "seat-change") {
      if (seatRequests.status !== "success") return;
      const entry = seatRequests.data.find(
        (e) => `${e.seating.id}:${e.request.attendeeId}` === row.id,
      );
      if (!entry) return;
      setAnswering(answering);
      void command.submit("DECIDE_SEAT_REQUEST", eventId, uid, {
        seatingId: entry.seating.id,
        attendeeId: entry.request.attendeeId,
        decision,
        toTableId: entry.request.toTableId,
      });
      return;
    }
    if (requests.status !== "success") return;
    const request = requests.data.find((r) => r.attendeeId === row.id);
    if (!request) return;
    setAnswering(answering);
    void command.submit("DECIDE_TRACK_SWITCH", eventId, uid, {
      attendeeId: request.attendeeId,
      decision,
      toTrackId: request.toTrackId,
      // The revision on screen: the trigger refuses the answer if the
      // attendee has asked again since, even for the same table.
      ...(request.requestedAt ? { requestedAt: request.requestedAt } : {}),
    });
  };

  return (
    <div className="space-y-4" data-testid="requests-content">
      <p className="text-sm text-muted-foreground">
        Attendees ask here for a theme table switch (or for a theme table when
        they have none), and for a dinner seat change in a published seating.
        They get a notification with your answer.
      </p>
      <AdminCommandFeedback
        state={command.state}
        onReset={command.reset}
        testId="request-decision"
      />
      {problems.map((problem) => (
        <WarningNote key={problem} role="alert" testId="requests-load-problem">
          {problem}
        </WarningNote>
      ))}
      {stillLoading && (
        <p
          className="text-sm text-muted-foreground"
          data-testid="requests-loading"
        >
          Loading more requests…
        </p>
      )}
      {nothingLoaded ? (
        <EmptyState icon={AlertCircle} title="Could not load requests">
          Reload the page to try again.
        </EmptyState>
      ) : (
        <RequestList
          eventId={eventId}
          rows={rows}
          timeZone={timeZone}
          emptyTitle="No open requests"
          emptyText="New requests appear here as attendees make them."
          renderActions={(row) => (
            <div className="flex flex-wrap gap-2">
              <Button
                size="sm"
                variant="secondary"
                onClick={() => decide(row, "ACCEPT")}
                disabled={busy}
                data-testid="request-accept"
              >
                <Check className="h-4 w-4" />
                {answering === `${row.type}:${row.id}:ACCEPT`
                  ? "Accepting…"
                  : "Accept"}
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => decide(row, "DECLINE")}
                disabled={busy}
                data-testid="request-decline"
              >
                <X className="h-4 w-4" />
                {answering === `${row.type}:${row.id}:DECLINE`
                  ? "Declining…"
                  : "Decline"}
              </Button>
            </div>
          )}
        />
      )}
    </div>
  );
}
